import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { InputLabel, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AuthModule from '../../auth/AuthModule';
import { useContactFormular } from '../../services/contactForm';
import { useFilters } from '../../services/filters';
import { CustomButton } from '../common/button/customButton';
import { CountryEmailMapping } from './countryEmailMapping';

export interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  contractType?: string;
  contractNumber?: string;
  contractUsageName?: string;
  equipmentNumber?: string;
  country?: string;
}

interface SignInNames {
  emailAddress: string;
}

interface TokenClaims {
  'signInNames.emailAddress': SignInNames;
}

export const ContactForm = (props: ContactFormProps) => {
  const {
    contractType: propContractType,
    contractUsageName,
    country: propCountry,
    open,
    onClose,
    contractNumber,
    equipmentNumber,
  } = props;

  const Account = AuthModule.getInstance().getAccount();
  const { filters } = useFilters();

  const { callAPI } = useContactFormular();
  const { t } = useTranslation();
  const [country, setCountry] = React.useState('');
  const [contractType, setContractType] = React.useState('');
  const [messageText, setMessageText] = React.useState('');

  useEffect(() => {
    let contractTypeTmp: string | undefined = '';
    if (propContractType) {
      if (contractUsageName === 'pbth') {
        contractTypeTmp = 'pbth';
      } else {
        contractTypeTmp = propContractType;
      }
      setContractType(contractTypeTmp as string);
    }
    // country
    if (propCountry) {
      setCountry(propCountry as string);
    }
  }, [contractType, country, contractUsageName, propContractType, propCountry]);

  const handleCountryChange = (event: SelectChangeEvent) => {
    setCountry(event.target.value as string);
  };

  const handleContractTypeChange = (event: SelectChangeEvent) => {
    setContractType(event.target.value as string);
  };

  const handleMessageTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setMessageText(event.target.value as string);
  };

  return (
    <Dialog fullWidth open={open} maxWidth="xs">
      <DialogTitle sx={{ fontSize: 36 }}>{t('Contact')}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {contractNumber && (
            <FormControl required fullWidth margin="normal">
              <TextField
                className="TextField-without-border-radius"
                label={t('ContractNumber')}
                required
                id="outlined-basic"
                variant="outlined"
                value={contractNumber}
                disabled
              />
            </FormControl>
          )}
          <FormControl required fullWidth margin="normal">
            <InputLabel id="countrySelectLabel">{t('Country')}</InputLabel>
            <Select
              className="TextField-without-border-radius"
              label={t('Country')}
              labelId="countrySelectLabel"
              id="countrySelect"
              value={country}
              variant="outlined"
              onChange={handleCountryChange}
              disabled={!!contractNumber}
            >
              {CountryEmailMapping.map((countryEmail) => {
                return (
                  <MenuItem
                    key={countryEmail.Country}
                    value={countryEmail.Country}
                  >
                    {t(countryEmail.Country)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl required fullWidth margin="normal">
            <InputLabel id="contractTypeSelectLabel">
              {t('ContractType')}
            </InputLabel>
            <Select
              className="TextField-without-border-radius"
              label={t('ContractType')}
              labelId="contractTypeSelectLabel"
              id="contractTypeSelect"
              value={contractType}
              variant="outlined"
              onChange={handleContractTypeChange}
              disabled={!!contractNumber}
            >
              <MenuItem key="str" value="str">
                {t('STR')}
              </MenuItem>
              <MenuItem key="pbth" value="pbth">
                {t('PBTH')}
              </MenuItem>
              <MenuItem key="rfm" value="rfm">
                {t('RFM')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              className="TextField-without-border-radius"
              required
              multiline
              id="outlined-basic"
              label="Text"
              variant="outlined"
              minRows={5}
              maxRows={5}
              onChange={handleMessageTextChange}
            />
          </FormControl>
          <DialogActions
            style={{
              paddingTop: '2vh',
              paddingLeft: 0,
              paddingRight: 0,
              // justifyContent: 'space-between',
              alignContent: 'flex-end',
              width: '100%',
            }}
          >
            <CustomButton
              handleOnClick={() => {
                onClose();
              }}
              text={t('Close')}
              sx={{
                fontFamily: 'JHFontBold',
                border: '1px solid transparent',
              }}
              color="primary"
              size="small"
              endIcon={<CloseIcon />}
            />

            <CustomButton
              handleOnClick={() => {
                callAPI({
                  Country: country,
                  ContractType: contractType,
                  Message: messageText,
                  Name: Account.name,
                  Email: (Account.idTokenClaims as TokenClaims)[
                    'signInNames.emailAddress'
                  ],
                  KH1: filters.label,
                  ContractNumber: contractNumber,
                  Equipment: equipmentNumber,
                });
                onClose();
              }}
              disabled={
                country === '' || contractType === '' || messageText === ''
              }
              text={t('Send')}
              sx={{
                fontFamily: 'JHFontBold',
                border: '1px solid transparent',
              }}
              color="secondary"
              size="small"
              endIcon={<SendIcon />}
            />
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ContactForm.defaultProps = {
  contractType: null,
  contractNumber: null,
  contractUsageName: null,
  equipmentNumber: null,
  country: null,
};
